import { useEffect, useMemo, useState } from "react";

import { resolveApprovalRequestTypeLabel } from "@/pages/AdminV2/ApprovalsQueueSections";
import {
  useApproveAdminV2ApprovalMutation,
  useCancelAdminV2ApprovalMutation,
  useGetAdminV2ApprovalsQuery,
  useGetAdminV2MeQuery,
  useRejectAdminV2ApprovalMutation,
} from "@/store/api/baseApi";
import type { AdminV2ApprovalStatus } from "@/types/adminV2";
import FrontendLoggerFactory from "@/utils/logging/FrontendLoggerFactory";
import {
  VNextButton,
  VNextCard,
  VNextField,
  VNextKeyValueList,
  VNextNotice,
  VNextPill,
  VNextSelect,
  VNextTextArea,
} from "@/vnext/primitives";
import { SettingsWorkspaceChrome } from "@/vnext/routes/settings/SettingsWorkspaceChrome";
import {
  formatVNextDateTime,
  formatVNextLabel,
} from "@/vnext/shared/formatters";

type ApprovalFilterValue = "all" | AdminV2ApprovalStatus;

const APPROVAL_FILTERS: ApprovalFilterValue[] = [
  "pending",
  "approved",
  "rejected",
  "cancelled",
  "all",
];

const logger =
  FrontendLoggerFactory.getLogger().withComponent("ApprovalsQueuePage");

const APPROVAL_ACTION_SUCCESS_COPY = {
  approve: "Approval request approved.",
  reject: "Approval request rejected.",
  cancel: "Approval request cancelled.",
} as const;

const APPROVAL_ACTION_FAILURE_COPY = {
  approve: "Could not approve approval request.",
  reject: "Could not reject approval request.",
  cancel: "Could not cancel approval request.",
} as const;

const APPROVAL_RESOLVED_ACTION_COPY = "Only pending requests can be changed.";

const APPROVAL_PERMISSION_LABELS: Record<string, string> = {
  "approvals.approve": "Can approve requests",
  "approvals.view": "Can view approvals",
};

const formatOptionalValue = (value: string | null | undefined): string =>
  value && value.trim().length > 0 ? value : "Not recorded";

const formatApprovalKey = (value: string): string =>
  APPROVAL_PERMISSION_LABELS[value] ??
  value
    .split(/[._-]+/)
    .filter((part) => part.length > 0)
    .map(formatVNextLabel)
    .join(" ");

const formatApprovalTarget = (approval: {
  targetId: string | null;
  targetType: string | null;
}): string => {
  if (!approval.targetType && !approval.targetId) {
    return "No target recorded";
  }

  if (!approval.targetType) {
    return approval.targetId ?? "No target recorded";
  }

  if (!approval.targetId) {
    return formatApprovalKey(approval.targetType);
  }

  return `${formatApprovalKey(approval.targetType)} ${approval.targetId}`;
};

interface DecisionCommentDraft {
  approvalId: string | null;
  value: string;
  error: string | null;
}

export default function ApprovalsRoute() {
  const { data: meData } = useGetAdminV2MeQuery();
  const [statusFilter, setStatusFilter] =
    useState<ApprovalFilterValue>("pending");
  const [selectedApprovalId, setSelectedApprovalId] = useState<string | null>(
    null,
  );
  const [feedback, setFeedback] = useState<string | null>(null);
  const [decisionCommentDraft, setDecisionCommentDraft] =
    useState<DecisionCommentDraft>({
      approvalId: null,
      value: "",
      error: null,
    });
  const query = useGetAdminV2ApprovalsQuery(
    statusFilter === "all"
      ? { limit: 200 }
      : { limit: 200, status: statusFilter },
  );
  const approvals = useMemo(() => query.data?.approvals ?? [], [query.data]);
  const selectedApproval = useMemo(
    () =>
      approvals.find((approval) => approval.id === selectedApprovalId) ??
      (approvals.length > 0 ? approvals[0] : null),
    [approvals, selectedApprovalId],
  );
  const selectedApprovalKey = selectedApproval?.id ?? null;
  const decisionComment =
    decisionCommentDraft.approvalId === selectedApprovalKey
      ? decisionCommentDraft.value
      : "";
  const decisionCommentError =
    decisionCommentDraft.approvalId === selectedApprovalKey
      ? decisionCommentDraft.error
      : null;

  const setDecisionCommentForSelection = (value: string): void => {
    setDecisionCommentDraft((current) => ({
      approvalId: selectedApprovalKey,
      value,
      error: current.approvalId === selectedApprovalKey ? current.error : null,
    }));
  };

  const setDecisionCommentErrorForSelection = (error: string | null): void => {
    setDecisionCommentDraft((current) => ({
      approvalId: selectedApprovalKey,
      value: current.approvalId === selectedApprovalKey ? current.value : "",
      error,
    }));
  };

  const resetDecisionCommentForSelection = (): void => {
    setDecisionCommentDraft({
      approvalId: selectedApprovalKey,
      value: "",
      error: null,
    });
  };
  const [approveApproval, approveState] = useApproveAdminV2ApprovalMutation();
  const [rejectApproval, rejectState] = useRejectAdminV2ApprovalMutation();
  const [cancelApproval, cancelState] = useCancelAdminV2ApprovalMutation();
  const canApprove = (meData?.permissionKeys ?? []).includes(
    "approvals.approve",
  );
  const isSelectedApprovalPending = selectedApproval?.status === "pending";
  const canCancelSelected = selectedApproval
    ? isSelectedApprovalPending &&
      (canApprove ||
        selectedApproval.requestedByStaffUserId === meData?.staffUser.id)
    : false;
  const resolvedApprovalActionReason =
    selectedApproval && !isSelectedApprovalPending
      ? `${APPROVAL_RESOLVED_ACTION_COPY} This request is ${formatVNextLabel(
          selectedApproval.status,
        ).toLowerCase()}.`
      : null;
  const emptyQueueMessage =
    statusFilter === "pending"
      ? "No approval requests are waiting right now. Switch to All to review resolved requests."
      : statusFilter === "all"
        ? "No approval requests have been recorded yet."
        : `No ${formatVNextLabel(statusFilter).toLowerCase()} requests match this filter. Switch to All to review the full queue.`;

  useEffect(() => {
    logger.debug("APPROVALS: page enter", {
      feature: "FND",
      operation: "APPROVALS_PAGE_ENTER",
      component: "ApprovalsQueuePage",
    });
  }, []);

  useEffect(() => {
    if (query.isLoading || query.isError) {
      return;
    }

    logger.debug("APPROVALS: page ready", {
      feature: "FND",
      operation: "APPROVALS_PAGE_READY",
      component: "ApprovalsQueuePage",
      approvalCount: approvals.length,
      statusFilter,
      selectedApprovalId: selectedApproval?.id ?? null,
    });
  }, [
    approvals.length,
    query.isError,
    query.isLoading,
    selectedApproval?.id,
    statusFilter,
  ]);

  const runApprovalAction = async (action: "approve" | "reject" | "cancel") => {
    if (!selectedApproval) {
      return;
    }

    if (selectedApproval.status !== "pending") {
      setFeedback(APPROVAL_RESOLVED_ACTION_COPY);
      return;
    }

    const trimmedDecisionComment = decisionComment.trim();
    if (action !== "approve" && trimmedDecisionComment.length === 0) {
      setDecisionCommentErrorForSelection(
        "Add a decision comment before rejecting or cancelling.",
      );
      return;
    }

    setFeedback(null);
    setDecisionCommentErrorForSelection(null);
    const transitionPayload = {
      approvalRequestId: selectedApproval.id,
      organisationId: selectedApproval.organisationId,
      ...(trimmedDecisionComment.length > 0
        ? { comment: trimmedDecisionComment }
        : {}),
    };

    try {
      if (action === "approve") {
        logger.debug("APPROVALS: approve start", {
          feature: "FND",
          operation: "APPROVALS_APPROVE_START",
          component: "ApprovalsQueuePage",
          approvalRequestId: selectedApproval.id,
        });
        await approveApproval(transitionPayload).unwrap();
        logger.debug("APPROVALS: approve success", {
          feature: "FND",
          operation: "APPROVALS_APPROVE_SUCCESS",
          component: "ApprovalsQueuePage",
          approvalRequestId: selectedApproval.id,
        });
      } else if (action === "reject") {
        await rejectApproval(transitionPayload).unwrap();
      } else {
        await cancelApproval(transitionPayload).unwrap();
      }
      setFeedback(APPROVAL_ACTION_SUCCESS_COPY[action]);
      resetDecisionCommentForSelection();
      await query.refetch();
    } catch {
      if (action === "approve") {
        logger.error("APPROVALS: approve failed", new Error("approve_failed"), {
          feature: "FND",
          operation: "APPROVALS_APPROVE_FAIL",
          component: "ApprovalsQueuePage",
          approvalRequestId: selectedApproval.id,
        });
      }
      setFeedback(APPROVAL_ACTION_FAILURE_COPY[action]);
    }
  };

  return (
    <SettingsWorkspaceChrome
      subtitle="Review approval requests and resolve privileged actions."
      title="Approvals"
    >
      <div className="vnext-stack">
        {feedback ? <VNextNotice>{feedback}</VNextNotice> : null}
        <div className="vnext-two-pane">
          <VNextCard
            actions={
              <VNextButton
                onClick={() => void query.refetch()}
                tone="secondary"
              >
                Refresh
              </VNextButton>
            }
            subtitle="Pending is the default view. Switch to All when you need resolved history."
            title="Requests"
          >
            <div className="vnext-grid">
              <label className="vnext-field">
                <span className="vnext-field__label">Status</span>
                <VNextSelect
                  onChange={(event) => {
                    const nextFilter = event.target
                      .value as ApprovalFilterValue;
                    logger.debug("APPROVALS: filter changed", {
                      feature: "FND",
                      operation: "APPROVALS_FILTER_CHANGED",
                      component: "ApprovalsQueuePage",
                      statusFilter: nextFilter,
                      source: "user",
                    });
                    setStatusFilter(nextFilter);
                  }}
                  value={statusFilter}
                >
                  {APPROVAL_FILTERS.map((option) => (
                    <option key={option} value={option}>
                      {formatVNextLabel(option)}
                    </option>
                  ))}
                </VNextSelect>
              </label>

              {query.isLoading ? (
                <div className="vnext-empty">Loading approval requests…</div>
              ) : query.isError ? (
                <VNextNotice tone="danger">
                  Approval requests could not be loaded.
                </VNextNotice>
              ) : approvals.length === 0 ? (
                <div className="vnext-empty">{emptyQueueMessage}</div>
              ) : (
                <div className="vnext-scroll-panel vnext-grid">
                  {approvals.map((approval) => (
                    <button
                      className={`vnext-list-card${
                        selectedApproval?.id === approval.id ? " is-active" : ""
                      }`}
                      key={approval.id}
                      onClick={() => {
                        logger.debug("APPROVALS: selection changed", {
                          feature: "FND",
                          operation: "APPROVALS_SELECTION_CHANGED",
                          component: "ApprovalsQueuePage",
                          approvalRequestId: approval.id,
                          source: "user",
                        });
                        setSelectedApprovalId(approval.id);
                      }}
                      type="button"
                    >
                      <div className="vnext-list-card__title">
                        {resolveApprovalRequestTypeLabel(approval.requestType)}
                      </div>
                      <div className="vnext-list-card__meta">
                        {approval.reason}
                      </div>
                      <div className="vnext-chip-list">
                        <VNextPill tone="accent">
                          {formatVNextLabel(approval.status)}
                        </VNextPill>
                        <VNextPill>
                          {approval.targetType
                            ? formatApprovalKey(approval.targetType)
                            : "No target"}
                        </VNextPill>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </VNextCard>

          <VNextCard
            subtitle="Inspect request details and take the next action."
            title={
              selectedApproval
                ? resolveApprovalRequestTypeLabel(selectedApproval.requestType)
                : "Selected approval"
            }
          >
            {!selectedApproval ? (
              <div className="vnext-empty">
                Select a request to review the reason, safe details, and next
                action.
              </div>
            ) : (
              <div className="vnext-grid">
                <VNextKeyValueList
                  items={[
                    {
                      label: "Request type",
                      value: resolveApprovalRequestTypeLabel(
                        selectedApproval.requestType,
                      ),
                    },
                    {
                      label: "Target",
                      value: formatApprovalTarget(selectedApproval),
                    },
                    {
                      label: "Status",
                      value: formatVNextLabel(selectedApproval.status),
                    },
                    {
                      label: "Required permission",
                      value: selectedApproval.requiredPermissionKey
                        ? formatApprovalKey(
                            selectedApproval.requiredPermissionKey,
                          )
                        : "Approver permission not recorded",
                    },
                    {
                      label: "Requested by",
                      value:
                        selectedApproval.requestedByEmail ??
                        selectedApproval.requestedByFirebaseUid ??
                        selectedApproval.requestedByStaffUserId,
                    },
                    {
                      label: "Approver",
                      value:
                        selectedApproval.approverEmail ??
                        selectedApproval.approverFirebaseUid ??
                        selectedApproval.approverStaffUserId ??
                        "Unassigned",
                    },
                    {
                      label: "Created",
                      value: formatVNextDateTime(selectedApproval.createdAt),
                    },
                    {
                      label: "Expires",
                      value: formatVNextDateTime(selectedApproval.expiresAt),
                    },
                  ]}
                />
                <VNextNotice>
                  {formatOptionalValue(selectedApproval.payloadSummary.message)}
                </VNextNotice>
                {(selectedApproval.payloadSummary.safeFields?.length ?? 0) >
                0 ? (
                  <VNextKeyValueList
                    items={selectedApproval.payloadSummary.safeFields ?? []}
                  />
                ) : null}
                <div>
                  <div className="vnext-field__label">Reason</div>
                  <VNextTextArea
                    readOnly
                    rows={4}
                    value={selectedApproval.reason}
                  />
                </div>
                {resolvedApprovalActionReason ? (
                  <VNextNotice tone="warning">
                    {resolvedApprovalActionReason}
                  </VNextNotice>
                ) : null}
                <VNextField
                  error={decisionCommentError}
                  hint={
                    isSelectedApprovalPending
                      ? "Required when rejecting or cancelling. Optional when approving."
                      : "Closed requests do not need a decision comment."
                  }
                  label="Decision comment"
                >
                  <VNextTextArea
                    disabled={!isSelectedApprovalPending}
                    onChange={(event) => {
                      setDecisionCommentForSelection(event.target.value);
                      if (decisionCommentError) {
                        setDecisionCommentErrorForSelection(null);
                      }
                    }}
                    rows={3}
                    value={decisionComment}
                  />
                </VNextField>
                <div className="vnext-inline-actions">
                  <VNextButton
                    disabled={
                      !isSelectedApprovalPending ||
                      !canApprove ||
                      approveState.isLoading
                    }
                    onClick={() => void runApprovalAction("approve")}
                    tone="primary"
                  >
                    Approve
                  </VNextButton>
                  <VNextButton
                    disabled={
                      !isSelectedApprovalPending ||
                      !canApprove ||
                      rejectState.isLoading
                    }
                    onClick={() => void runApprovalAction("reject")}
                    tone="ghost"
                  >
                    Reject
                  </VNextButton>
                  <VNextButton
                    disabled={!canCancelSelected || cancelState.isLoading}
                    onClick={() => void runApprovalAction("cancel")}
                    tone="secondary"
                  >
                    Cancel
                  </VNextButton>
                </div>
              </div>
            )}
          </VNextCard>
        </div>
      </div>
    </SettingsWorkspaceChrome>
  );
}
